// Shared URL-hash protocol for the Release Checklist surfaces.
//
// Hash schema: `#?release=<r>&owner=<entityRef>&state=<csv>`
//   - `release` is the only required key — `release` alone is enough to deep
//     link to a checklist.
//   - `state` is a comma-separated list of FilterChip values (e.g.
//     `tagged,non_repo`). Unknown tokens are dropped at parse time.
//   - The bare-release form (`#<release>` without `?`) is recognised as a
//     legacy shorthand so previously-shared URLs keep working.
//
// Both /release-checklist (the main page) and /release-checklist-backups
// (the backups page) parse the same hash, so a user can round-trip between
// the two routes without losing their selection. The main page additionally
// *writes* the hash (filter changes, release switches); the backups page is
// read-only and only cares about `release`.

import type { FilterChip } from './ReleaseManagement/Filters';

export const FILTER_CHIP_VALUES: ReadonlySet<FilterChip> = new Set<FilterChip>([
  'not_ready',
  'no_changes',
  'tagged',
  'non_repo',
]);

export interface HashState {
  release: string | null;
  owner: string | null;
  state: Set<FilterChip>;
}

export const EMPTY_HASH_STATE: HashState = {
  release: null,
  owner: null,
  state: new Set(),
};

export const readHash = (): HashState => {
  if (typeof window === 'undefined') return EMPTY_HASH_STATE;
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw) return EMPTY_HASH_STATE;

  // Legacy `#<release>` form: anything without a leading `?` is the bare
  // release name. `decodeURIComponent` here mirrors the original behaviour.
  if (!raw.startsWith('?')) {
    try {
      return { ...EMPTY_HASH_STATE, release: decodeURIComponent(raw) };
    } catch {
      return { ...EMPTY_HASH_STATE, release: raw };
    }
  }

  const params = new URLSearchParams(raw.slice(1));
  const state = new Set<FilterChip>();
  const stateRaw = params.get('state');
  if (stateRaw) {
    for (const token of stateRaw.split(',')) {
      if (FILTER_CHIP_VALUES.has(token as FilterChip)) {
        state.add(token as FilterChip);
      }
    }
  }
  return {
    release: params.get('release') || null,
    owner: params.get('owner') || null,
    state,
  };
};

// Narrow accessor for surfaces that only need the release (e.g. the backups
// page). Returns null when the hash is empty or has no `release` key.
export const readReleaseFromHash = (): string | null => readHash().release;

export const buildHash = (s: HashState): string => {
  if (!s.release) return '';
  const params = new URLSearchParams();
  params.set('release', s.release);
  if (s.owner) params.set('owner', s.owner);
  if (s.state.size > 0) {
    // Sort for stable URLs — chip toggle order shouldn't change the hash.
    const ordered = Array.from(s.state).sort();
    params.set('state', ordered.join(','));
  }
  return `#?${params.toString()}`;
};

// Equality check used to suppress redundant pushState/replaceState writes
// (which would otherwise echo back through the hashchange listener and
// re-run setState on every effect tick).
export const hashEqual = (a: HashState, b: HashState): boolean => {
  if (a.release !== b.release || a.owner !== b.owner) return false;
  if (a.state.size !== b.state.size) return false;
  for (const v of a.state) if (!b.state.has(v)) return false;
  return true;
};
